import React from 'react';
import { useCSS } from '../../lib/hooks';
import { i18nIntl, autotranslateText, isEmpty } from '../../lib/ep';
import { injectIntl } from 'react-intl';

import ActionLink from '../builder/ActionLink';
import Tooltip from '../builder/Tooltip';
import { Link } from 'react-router-dom';

const detect_if_disabled = (reason, promotion) => {
	if(reason == undefined || promotion == undefined) return false;

	const platform = parseInt(promotion?.promo_type ?? '0');
	const public_entries = (promotion.public_entries != '0') ? true : false;
	const votable_entries = (promotion.votable_entries != '0') ? true : false;
	const with_my_networks = promotion?.my_networks ?? false;
	const instant_enable = promotion?.instant_enable ?? false;
	const is_media = (promotion.promotion_type_id == '36' || promotion.promotion_type_id == '37') ? true : false;
	
	if(reason == 'p1' && platform == 0) return true;		// Premium needed
	if(reason == 'p2' && platform <= 1) return true;		// WhiteLabel needed
	if(reason == 'p3' && platform <= 2) return true;		// Corporate needed
	if(reason == 'p4' && !public_entries) return true;		// Public entries needed
	if(reason == 'p5' && is_media) return true;				// Not media contest
	if(reason == 'p6' && !votable_entries) return true;		// Votable entries needed
	if(reason == 'p7' && !with_my_networks) return true;	// My networks not enabled
	if(reason == 'p8' && !instant_enable) return true;		// Instan Win not enabled

	return false;
};

const LinkWrapper = ({ children, link_type, _class, _href, ...props }) => {
	if(link_type == undefined) link_type = 'a';
	if(link_type == 'link' && _href != undefined) {
		// router-link
		return (
			<Link to={_href} className='card_link submit_link in_top' {...props}>{children}</Link>
		)
	}

	return (
		<ActionLink _class={_class} _href={_href} {...props}>{children}</ActionLink>
	)
};

const CardWrapper = ({ disabled, reason, children, intl, ...props }) => {
	let wrapper_props = {
		_class:{
			other:'card_link',
		},
	};

	if(!disabled) {
		return (
			<LinkWrapper {...wrapper_props} {...props}>
				{children}
			</LinkWrapper>
		)
	}

	const i18n = (m, c) => i18nIntl(m, intl, c);

	let error_msg = false;
	switch(reason) {
		case 'p1':
			error_msg = i18n("This option is only available in %s version").replace("%s", i18n("Premium"));
			break;

		case 'p2':
			error_msg = i18n("This option is only available in %s version").replace("%s", i18n("White Label"));
			break;

		case 'p3':
			error_msg = i18n("This option is only available in %s version").replace("%s", i18n("Corporate"));
			break;

		case 'p4':
			error_msg = i18n("This option is only available in promotions with gallery enable");
			break;

		case 'p5':
			error_msg = i18n("This option is not available in promotions with media contest");
			break;

		default:
			error_msg = i18n("This is not available");
			break;
	}

	if(props.custom_disabled_msg != undefined && props.custom_disabled_msg != "") error_msg = props.custom_disabled_msg;

	wrapper_props.tooltip = <Tooltip autotranslate={false} position="top" with_icon={false}>{error_msg}</Tooltip>;

	return (
		<LinkWrapper {...wrapper_props}>
			{children}
		</LinkWrapper>
	)
};

export const CardItem = ({ item, promotion, ...props }) => {
	/*
	– title					[string]	[mandatory]
	– description			[text]		[optional]	[default: null]
	– ribbon				[text]		[optional]	[default: null]
	– autotranslate			[boolean]	[optional]	[default: true]
	– enabled				[boolean]	[optional]	[default: false]
	– disabled				[boolean]	[optional]	[default: false]		(forced disabled status)
	– addon					[boolean]	[optional]	[default: false]
	– reason				[text]		[optional]	[default: null]			(minimum platform to disable)
	– target				[string]	[optional]	[default: '_self']		(only if "link" exists)
	– style					[array]		[optional]	[default: null]
	– _class				[object]	[optional]	[default: null]
	– alt					[string]	[optional]	[default: item.title]	(only if "image" exists)
	– extra_content			[object]	[optional]	[default: null]
	– custom_disabled_msg	[text]		[optional]	[default: null]			(displayed in replacement of the default disabled text message)

	// one of these items must exist:
	– link			[url]		[optional]
	– onClick		[function]	[optional]

	// one of these items must exist:
	– icon			[string]	[optional]
	– image			[string]	[optional]
	*/

	if(isEmpty(item, 'title')) return null;			// title is mandatory
	if(item.icon == undefined && item.custom_icon == undefined && item.image == undefined) return null;		// icon or image is mandatory
	if(item.link == undefined && item.onClick == undefined) return null;	// link or onClick is mandatory
	const autotranslate = item?.autotranslate ?? true;
	const ribbon = item?.ribbon ?? false;

	const title = autotranslateText(item.title, autotranslate, props.intl);
	
	const skip = item?.skip ?? false;
	if(skip) return null;

	const reason = item?.reason ?? false;
	const enabled = item?.enabled ?? false;
	const addon = item?.addon ?? false;

	const disabled = (promotion !== false) ? detect_if_disabled(reason, promotion) : false;
	const forced_disabled = item?.disabled ?? false;

	let li_classes = [];
	if(item._class != undefined && item._class.length > 0) li_classes = [...li_classes, ...item._class];
	if(disabled || forced_disabled) li_classes.push('disabledItem');
	if(enabled) li_classes.push('is_enabled');
	if(addon) li_classes.push('is_addon');
	if(item.image != undefined && typeof(item.image) != 'string') li_classes.push('with_full_image');

	return (
		<li class={li_classes.join(" ")}>
			<CardWrapper
				disabled={disabled || forced_disabled}
				reason={reason}
				_href={item.link}
				_target={item.target}
				onClick={item.onClick}
				intl={props.intl}
				link_type={props.link_type}
				custom_disabled_msg={item.custom_disabled_msg}
			>
				{(ribbon) && (
					<span class="type_ribbon">
						<span>{ribbon}</span>
					</span>
				)}

				{(item.icon != undefined && typeof(item.icon) == 'string') && (
					<span class={`b_icon is_icon ${item.icon}`} />
				)}

				{(item.custom_icon != undefined) && (
					<span class="b_icon is_combo">{item.custom_icon}</span>
				)}

				{(item.icon != undefined && typeof(item.icon) != 'string') && (
					<span class="b_icon with_multiicon">{item.icon}</span>
				)}

				{(item.image != undefined && typeof(item.image) == 'string') && (
					<span class="b_image">
						<img src={item.image} width="40" alt={item?.alt || title} />
					</span>
				)}

				{(item.image != undefined && typeof(item.image) != 'string') && (
					<span class="image">
						{item.image}
					</span>
				)}

				<strong class="b_title">{title}</strong>

				{(item.description != undefined && item.description != "") && (
					<p class="desc" dangerouslySetInnerHTML={{ __html:autotranslateText(item.description, autotranslate, props.intl) }} />
				)}

				{(item.extra_content != undefined) && (
					<div class="ctas gui_not_in_top_small">
						{item.extra_content}
					</div>
				)}
			</CardWrapper>
		</li>
	)
};

const CardSelector = ({ items, ...props }) => {
	if(items == undefined || items.length == 0) return null;

	const promotion = props?.promotion ?? false;
	//if(promotion === false) return null;	// we need to enable this for non-promotion pages (i.e. "/utilities")
	useCSS('comp/cards.css');

	let classes = ['gui_grid', 'cards_selector'];
	if(props._class != undefined) classes.push(props._class);

	return (
		<ul class={classes.join(" ")} style={props.style}>
			{items.map((item, i) => {
				const hide = item?.hide ?? false;
				if(hide) return null;

				return (
					<CardItem
						key={`item_${i}`}
						item={item}
						promotion={promotion}
						intl={props.intl}
						link_type={props.link_type}
					/>
				)
			})}
		</ul>
	)
};

export default injectIntl(CardSelector);